import { useForm } from "@tanstack/react-form";
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation, useQuery } from "convex/react";
import { PlayIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { formatRelativeTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import RunScenarioDialog from "./RunScenarioDialog";
import { RunsPanel } from "./RunsPanel";

export default function ScenariosPage() {
	const scenarios = useQuery(api.scenarios.list) ?? [];
	const seed = useMutation(api.scenarios.seedDemoScenario);
	const create = useMutation(api.scenarios.create);
	const [selectedId, setSelectedId] = useState<Id<"scenarios"> | null>(null);

	const { mutate: runSeed, isPending: seeding } = useReactQueryMutation({
		mutationFn: async () => await seed({}),
		onSuccess: (id) => setSelectedId(id),
	});

	const { mutate: runCreate, isPending: creating } = useReactQueryMutation({
		mutationFn: async () =>
			await create({
				name: "Untitled scenario",
				senderName: "",
				senderEmail: "",
				initialSubject: "",
				triggerBody: "",
				personaDescription: "",
			}),
		onSuccess: (id) => setSelectedId(id),
	});

	const activeId =
		selectedId ?? (scenarios.length > 0 ? scenarios[0]._id : null);

	return (
		<div className="flex h-full flex-col">
			<header className="flex items-center justify-between border-b py-3 pl-14 pr-6">
				<div>
					<h1 className="text-lg font-semibold">Scenarios</h1>
					<p className="text-xs text-muted-foreground">
						Saved agentic agent scenarios. Run from here to drive a real agent
						thread against an LLM-driven persona (no real Outlook side effects).
					</p>
				</div>
				<Button onClick={() => runCreate()} disabled={creating}>
					<PlusIcon className="size-4" />
					{creating ? "Creating..." : "New scenario"}
				</Button>
			</header>
			<div className="flex flex-1 overflow-hidden">
				<aside className="w-80 shrink-0 border-r">
					<ScrollArea className="h-full">
						{scenarios.length === 0 ? (
							<div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
								<p className="text-sm text-muted-foreground">
									No scenarios yet. Seed the demo set to populate a variety of
									agentic conversations end-to-end.
								</p>
								<Button
									variant="outline"
									onClick={() => runSeed()}
									disabled={seeding}
								>
									{seeding ? "Seeding..." : "Seed example scenarios"}
								</Button>
							</div>
						) : (
							<ul className="divide-y">
								{scenarios.map((s) => (
									<li key={s._id}>
										<Button
											type="button"
											variant="ghost"
											onClick={() => setSelectedId(s._id)}
											className={cn(
												"flex h-auto w-full flex-col items-start justify-start gap-1 whitespace-normal rounded-none p-3 text-left font-normal",
												activeId === s._id && "bg-muted",
											)}
										>
											<span className="text-sm font-medium">{s.name}</span>
											<span className="text-xs text-muted-foreground">
												{s.senderName || "(no sender)"}
											</span>
											<span className="text-xs text-muted-foreground">
												Updated {formatRelativeTime(s.updatedAt)}
											</span>
										</Button>
									</li>
								))}
							</ul>
						)}
					</ScrollArea>
				</aside>
				<main className="flex-1 overflow-hidden">
					{activeId ? (
						<ScenarioEditor
							key={activeId}
							scenarioId={activeId}
							onDeleted={() => setSelectedId(null)}
						/>
					) : (
						<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
							Select a scenario to edit, or create a new one.
						</div>
					)}
				</main>
			</div>
		</div>
	);
}

// ScenarioEditor — Edit + Runs tabs for the selected scenario

function ScenarioEditor({
	scenarioId,
	onDeleted,
}: {
	scenarioId: Id<"scenarios">;
	onDeleted: () => void;
}) {
	const data = useQuery(api.scenarios.get, { id: scenarioId });
	const [runDialogOpen, setRunDialogOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<"edit" | "runs">("edit");
	const [selectedRunId, setSelectedRunId] = useState<Id<"scenarioRuns"> | null>(
		null,
	);

	if (data === undefined) {
		return (
			<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
				Loading…
			</div>
		);
	}
	if (data === null) {
		return (
			<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
				Scenario not found.
			</div>
		);
	}

	const { scenario, recentRuns } = data;
	const activeRunId = selectedRunId ?? recentRuns[0]?._id ?? null;

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center justify-between border-b py-3 pl-6 pr-6">
				<div>
					<h2 className="text-base font-semibold">{scenario.name}</h2>
					<p className="text-xs text-muted-foreground">
						Agentic · From {scenario.senderName} &lt;{scenario.senderEmail}&gt;
					</p>
				</div>
				<Button onClick={() => setRunDialogOpen(true)}>
					<PlayIcon className="size-4" />
					Run scenario
				</Button>
			</div>
			<Tabs
				value={activeTab}
				onValueChange={(v) => setActiveTab(v as "edit" | "runs")}
				className="flex flex-1 flex-col overflow-hidden"
			>
				<TabsList className="mx-6 mt-3 self-start">
					<TabsTrigger value="edit">Edit</TabsTrigger>
					<TabsTrigger value="runs">
						Runs
						{recentRuns.length > 0 ? (
							<Badge variant="secondary" className="ml-2">
								{recentRuns.length}
							</Badge>
						) : null}
					</TabsTrigger>
				</TabsList>
				<TabsContent value="edit" className="flex-1 overflow-hidden">
					<ScrollArea className="h-full">
						<EditForm scenario={scenario} onDeleted={onDeleted} />
					</ScrollArea>
				</TabsContent>
				<TabsContent value="runs" className="flex-1 overflow-hidden">
					<RunsPanel
						runs={recentRuns}
						selectedRunId={activeRunId}
						onSelectRun={setSelectedRunId}
					/>
				</TabsContent>
			</Tabs>
			<RunScenarioDialog
				scenarioId={scenarioId}
				open={runDialogOpen}
				onOpenChange={setRunDialogOpen}
				onRunStarted={(_threadId, runId) => {
					setActiveTab("runs");
					setSelectedRunId(runId);
				}}
			/>
		</div>
	);
}

// EditForm

type ScenarioForGet = NonNullable<
	ReturnType<typeof useQuery<typeof api.scenarios.get>>
>["scenario"];

function EditForm({
	scenario,
	onDeleted,
}: {
	scenario: ScenarioForGet;
	onDeleted: () => void;
}) {
	const update = useMutation(api.scenarios.update);
	const remove = useMutation(api.scenarios.remove);

	const form = useForm({
		defaultValues: {
			name: scenario.name,
			description: scenario.description ?? "",
			senderName: scenario.senderName,
			senderEmail: scenario.senderEmail,
			initialSubject: scenario.initialSubject,
			triggerBody: scenario.triggerBody,
			personaDescription: scenario.personaDescription,
		},
		onSubmit: async ({ value }) => {
			save({
				name: value.name,
				description: value.description,
				senderName: value.senderName,
				senderEmail: value.senderEmail,
				initialSubject: value.initialSubject,
				triggerBody: value.triggerBody,
				personaDescription: value.personaDescription,
			});
		},
	});

	const {
		mutate: save,
		isPending: saving,
		error: saveError,
	} = useReactQueryMutation({
		mutationFn: async (args: {
			name: string;
			description: string;
			senderName: string;
			senderEmail: string;
			initialSubject: string;
			triggerBody: string;
			personaDescription: string;
		}) => {
			await update({
				id: scenario._id,
				name: args.name,
				description: args.description,
				senderName: args.senderName,
				senderEmail: args.senderEmail,
				initialSubject: args.initialSubject,
				triggerBody: args.triggerBody,
				personaDescription: args.personaDescription,
			});
		},
	});

	const { mutate: del, isPending: deleting } = useReactQueryMutation({
		mutationFn: async () => await remove({ id: scenario._id }),
		onSuccess: () => onDeleted(),
	});

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				form.handleSubmit();
			}}
			className="space-y-6 p-6"
		>
			<FieldGroup>
				<form.Field name="name">
					{(f) => (
						<Field>
							<FieldLabel htmlFor="sc-name">Name</FieldLabel>
							<Input
								id="sc-name"
								value={f.state.value}
								onChange={(e) => f.handleChange(e.target.value)}
								onBlur={f.handleBlur}
							/>
						</Field>
					)}
				</form.Field>
				<form.Field name="description">
					{(f) => (
						<Field>
							<FieldLabel htmlFor="sc-desc">Description (optional)</FieldLabel>
							<Textarea
								id="sc-desc"
								value={f.state.value}
								onChange={(e) => f.handleChange(e.target.value)}
								onBlur={f.handleBlur}
								rows={2}
							/>
						</Field>
					)}
				</form.Field>
				<div className="grid grid-cols-2 gap-3">
					<form.Field name="senderName">
						{(f) => (
							<Field>
								<FieldLabel htmlFor="sc-sender-name">Sender name</FieldLabel>
								<Input
									id="sc-sender-name"
									value={f.state.value}
									onChange={(e) => f.handleChange(e.target.value)}
									onBlur={f.handleBlur}
									placeholder="David Chen"
								/>
							</Field>
						)}
					</form.Field>
					<form.Field name="senderEmail">
						{(f) => (
							<Field>
								<FieldLabel htmlFor="sc-sender-email">Sender email</FieldLabel>
								<Input
									id="sc-sender-email"
									type="email"
									value={f.state.value}
									onChange={(e) => f.handleChange(e.target.value)}
									onBlur={f.handleBlur}
									placeholder="david@example.com"
								/>
							</Field>
						)}
					</form.Field>
				</div>
				<form.Field name="initialSubject">
					{(f) => (
						<Field>
							<FieldLabel htmlFor="sc-subject">Initial subject</FieldLabel>
							<Input
								id="sc-subject"
								value={f.state.value}
								onChange={(e) => f.handleChange(e.target.value)}
								onBlur={f.handleBlur}
								placeholder="Can we set up a 30-minute call this week?"
							/>
							<FieldDescription>
								Persona replies will use "Re: {f.state.value || "…"}".
							</FieldDescription>
						</Field>
					)}
				</form.Field>
				<form.Field name="triggerBody">
					{(f) => (
						<Field>
							<FieldLabel htmlFor="sc-trigger-body">
								Initial email body
							</FieldLabel>
							<Textarea
								id="sc-trigger-body"
								value={f.state.value}
								onChange={(e) => f.handleChange(e.target.value)}
								onBlur={f.handleBlur}
								rows={8}
								placeholder="The first email from the sender that kicks off the scenario…"
							/>
							<FieldDescription>
								This is the single inbound email that triggers the drafter.
							</FieldDescription>
						</Field>
					)}
				</form.Field>
				<form.Field name="personaDescription">
					{(f) => (
						<Field>
							<FieldLabel>Persona behavior</FieldLabel>
							<MarkdownEditor
								value={f.state.value}
								onChange={(md) => f.handleChange(md)}
								placeholder="Describe who the sender is, what they want, and when the conversation should end."
								className="[&_.tiptap]:!min-h-[220px] [&_textarea]:!min-h-[220px]"
							/>
							<FieldDescription>
								An LLM plays this persona and replies dynamically to the
								drafter's output, up to the per-run turn cap.
							</FieldDescription>
						</Field>
					)}
				</form.Field>
			</FieldGroup>

			{saveError ? (
				<p className="text-sm text-destructive">
					{saveError instanceof Error ? saveError.message : String(saveError)}
				</p>
			) : null}

			<div className="flex items-center justify-between">
				<Button
					type="button"
					variant="outline"
					onClick={() => del()}
					disabled={deleting}
				>
					{deleting ? "Deleting..." : "Delete scenario"}
				</Button>
				<Button type="submit" disabled={saving}>
					{saving ? "Saving..." : "Save changes"}
				</Button>
			</div>
		</form>
	);
}
